import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Modal } from 'reactstrap';
import { Link, useLocation } from 'react-router-dom';
import { ChevronDown, ChevronRight, UserCircle2 } from 'lucide-react';
import { useStateContext } from '../Common/Context/StateContext';
import { usePermissions } from '../../Hooks/usePermissions';
import axiosInstance from '../../redux/api';
import LogoutModel from '../Common/Popup/LogoutModel';
import LogoutButton from './LogoutPage';
import SidebarFlyout from './SidebarFlyout';
import { useHoverIntent } from './useHoverIntent';
import { labelForPath } from './menu-config';
import {
  accordionKeysForPath,
  buildVisibleSections,
  collectPaths,
  isSingleLinkMenu,
  itemIsVisible,
  menuContainsPath,
  resolveActivePath,
} from './sidebarTree';
import './Sidebar.css';

/*
 * Primary navigation.
 *
 * Opens on hover and closes when the pointer leaves, unchanged. What changed is
 * everything around that:
 *
 * 1. Sidebar clicks are no longer swallowed. `.overlay` — the invisible
 *    full-screen catcher that dismisses filter dropdowns on three list screens
 *    — sat at the same z-index as `.sidebar` and won the tie on DOM order, so
 *    with a filter open anywhere the navigation silently stopped responding.
 *    Fixed in App.css; see the note on `.overlay` there.
 *
 * 2. Expanding no longer relayouts the page. `.right-side` keeps a fixed margin
 *    and the panel overlays it, so a mouse passing the left edge no longer
 *    forces a full document reflow and repaint for 200ms.
 *
 * 3. A collapsed group icon always does something — it opens a flyout rather
 *    than silently ignoring the click.
 */

const CONTACT_COUNT_PATH = 'admin/settings/contact-inquiries/count';

const Sidebar = () => {
  const location = useLocation();
  const { setCurrentUrl, setCurrentCardTitle } = useStateContext();
  const { hasAction, isSuperAdmin, currentUserId } = usePermissions();

  const [isLogoutOpen, setLogoutOpen] = useState(false);
  const [contactCount, setContactCount] = useState(0);
  const [openAccordions, setOpenAccordions] = useState({});
  const [flyoutKey, setFlyoutKey] = useState(null);

  const { isOpen: isExpanded, open, close, openNow, closeNow } =
    useHoverIntent();

  /* ---------- data ---------- */

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const response = await axiosInstance.get(CONTACT_COUNT_PATH);
        const count = response?.data?.response?.count;
        if (!cancelled && typeof count === 'number') setContactCount(count);
      } catch {
        /* silent — the badge is optional decoration, not information the
           navigation depends on */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  /* ---------- tree ---------- */

  const allPaths = useMemo(() => collectPaths(), []);
  const activePath = useMemo(
    () => resolveActivePath(allPaths, location.pathname),
    [allPaths, location.pathname],
  );

  const visibleSections = useMemo(
    () => buildVisibleSections({ hasAction, isSuperAdmin }),
    [hasAction, isSuperAdmin],
  );

  useEffect(() => {
    const match = labelForPath(location.pathname);
    if (match) {
      setCurrentUrl(match.label);
      setCurrentCardTitle(match.toplabel || '');
    }
  }, [location.pathname, setCurrentUrl, setCurrentCardTitle]);

  useEffect(() => {
    const keys = accordionKeysForPath(activePath);
    if (Object.keys(keys).length === 0) return;
    setOpenAccordions((previous) => ({ ...previous, ...keys }));
  }, [activePath]);

  /* ---------- interaction ---------- */

  // Collapsing while a flyout is open would leave it anchored to a moving
  // trigger, so the two states are mutually exclusive by construction.
  useEffect(() => {
    if (isExpanded) setFlyoutKey(null);
  }, [isExpanded]);

  useEffect(() => {
    const onKeyDown = (event) => {
      if (event.key !== 'Escape') return;
      setFlyoutKey(null);
      closeNow();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [closeNow]);

  // Navigating is a decision, so the panel gets out of the way immediately
  // rather than lingering for the close delay.
  const handleNavigate = useCallback(() => {
    setFlyoutKey(null);
    closeNow();
  }, [closeNow]);

  const toggleAccordion = useCallback(
    (key) =>
      setOpenAccordions((previous) => ({ ...previous, [key]: !previous[key] })),
    [],
  );

  const isActive = useCallback(
    (path) => Boolean(path) && path === activePath,
    [activePath],
  );

  /* ---------- rendering ---------- */

  const renderLeaf = (menu, item) => {
    if (!itemIsVisible({ item, menu, hasAction, isSuperAdmin })) return null;
    const active = isActive(item.path);
    return (
      <li key={item.path}>
        <Link
          to={item.path}
          onClick={handleNavigate}
          aria-current={active ? 'page' : undefined}
          className={`sidebar-item ${active ? 'active current' : ''}`}
        >
          <span className="sidebar-item-label">{item.label}</span>
        </Link>
      </li>
    );
  };

  const renderMenuItem = (menu, item, index) => {
    const hasChildren = Array.isArray(item.children) && item.children.length > 0;
    if (!hasChildren) return renderLeaf(menu, item);

    const key = `${menu.title}-${item.label}`;
    const isOpenAccordion = Boolean(openAccordions[key]);
    const children = item.children.filter((child) =>
      itemIsVisible({ item: child, menu, hasAction, isSuperAdmin }),
    );
    if (children.length === 0) return null;

    return (
      <li key={`${key}-${index}`}>
        <button
          type="button"
          className="sidebar-item"
          aria-expanded={isOpenAccordion}
          onClick={() => toggleAccordion(key)}
        >
          <span className="sidebar-item-label">{item.label}</span>
          <span className="sidebar-chevron" aria-hidden="true">
            {isOpenAccordion ? (
              <ChevronDown size={15} />
            ) : (
              <ChevronRight size={15} />
            )}
          </span>
        </button>
        {isOpenAccordion && (
          <ul className="sidebar-accordion-children">
            {children.map((child) => {
              const active = isActive(child.path);
              return (
                <li key={child.path}>
                  <Link
                    to={child.path}
                    onClick={handleNavigate}
                    aria-current={active ? 'page' : undefined}
                    className={`sidebar-accordion-child ${active ? 'active current' : ''}`}
                  >
                    {child.label}
                  </Link>
                </li>
              );
            })}
          </ul>
        )}
      </li>
    );
  };

  const renderMenu = (menu) => {
    const Icon = menu.icon;
    const active = menuContainsPath(menu, activePath);
    const badge = menu.showCount && contactCount > 0 ? contactCount : null;

    if (isSingleLinkMenu(menu)) {
      const item = menu.items[0];
      if (!itemIsVisible({ item, menu, hasAction, isSuperAdmin })) return null;
      return (
        <li key={menu.title}>
          <Link
            to={item.path}
            onClick={handleNavigate}
            aria-current={active ? 'page' : undefined}
            className={`sidebar-item sidebar-top-item ${active ? 'active current' : ''}`}
            title={!isExpanded ? menu.title : undefined}
          >
            <Icon size={20} aria-hidden="true" />
            <span className="sidebar-item-label">{menu.title}</span>
            {badge != null && (
              <span className="sidebar-count-badge">{badge}</span>
            )}
          </Link>
        </li>
      );
    }

    const key = `menu-${menu.title}`;
    const isOpenAccordion = Boolean(openAccordions[key]);

    /* Expanded: the button toggles its accordion inline.
     * Collapsed: it opens a flyout. Either way it does something — the old
     * handler was `isExpanded && toggle(...)`, a no-op on touch forever. */
    const trigger = (
      <button
        type="button"
        className={`sidebar-item sidebar-top-item ${active ? 'active' : ''}`}
        aria-expanded={isExpanded ? isOpenAccordion : undefined}
        aria-haspopup={!isExpanded ? 'menu' : undefined}
        title={!isExpanded ? menu.title : undefined}
        onClick={() => {
          if (isExpanded) toggleAccordion(key);
          else setFlyoutKey((current) => (current === menu.title ? null : menu.title));
        }}
      >
        <Icon size={20} aria-hidden="true" />
        <span className="sidebar-item-label">{menu.title}</span>
        {badge != null && <span className="sidebar-count-badge">{badge}</span>}
        <span className="sidebar-chevron" aria-hidden="true">
          {isOpenAccordion ? (
            <ChevronDown size={15} />
          ) : (
            <ChevronRight size={15} />
          )}
        </span>
      </button>
    );

    return (
      <li key={menu.title}>
        {isExpanded ? (
          trigger
        ) : (
          <SidebarFlyout
            menu={menu}
            isOpen={flyoutKey === menu.title}
            onOpenChange={(next) => setFlyoutKey(next ? menu.title : null)}
            hasAction={hasAction}
            isSuperAdmin={isSuperAdmin}
            activePath={activePath}
            onNavigate={handleNavigate}
          >
            {trigger}
          </SidebarFlyout>
        )}
        {/* Sub-items stay mounted while the accordion is open and are hidden by
          * CSS when the rail collapses, so a click already in flight during the
          * close animation still lands on a real element. */}
        {isOpenAccordion && (
          <ul className="sidebar-sub-items">
            {menu.items.map((item, index) => renderMenuItem(menu, item, index))}
          </ul>
        )}
      </li>
    );
  };

  const profilePath =
    currentUserId != null ? `/admin/update/${currentUserId}` : null;

  return (
    <>
      <Modal isOpen={isLogoutOpen} toggle={() => setLogoutOpen((v) => !v)}>
        <LogoutModel modal={isLogoutOpen} setModal={setLogoutOpen} />
      </Modal>

      <nav
        aria-label="Main navigation"
        className={`sidebar-new ${isExpanded ? 'expanded' : 'collapsed'}`}
        onMouseEnter={open}
        onMouseLeave={close}
        onFocusCapture={openNow}
        onBlurCapture={(event) => {
          // A click on a non-focusable child blurs to <body> while the pointer
          // is still inside, so both conditions have to hold before collapsing.
          if (
            !event.currentTarget.contains(event.relatedTarget) &&
            !event.currentTarget.matches(':hover')
          ) {
            close();
          }
        }}
      >
        <div className="sidebar-scroll">
          {visibleSections.map((section) => (
            <div key={section.label} className="sidebar-section">
              <div className="sidebar-section-label">{section.label}</div>
              <div className="sidebar-divider" />
              <ul className="sidebar-list">
                {section.items.map((menu) => renderMenu(menu))}
              </ul>
            </div>
          ))}
          {/* An account the server says has no menus. A missing profile never
            * reaches here: PrivateRoutes waits for the session first. */}
          {visibleSections.length === 0 && (
            <p className="sidebar-empty">
              No pages are assigned to your account yet. Ask a super admin to
              set your permissions.
            </p>
          )}
        </div>

        <div className="sidebar-footer">
          <ul className="sidebar-list">
            {profilePath && (
              <li>
                <Link
                  to={profilePath}
                  onClick={handleNavigate}
                  aria-current={
                    location.pathname === profilePath ? 'page' : undefined
                  }
                  className={`sidebar-item sidebar-top-item ${
                    location.pathname === profilePath ? 'active current' : ''
                  }`}
                  title={!isExpanded ? 'My Profile' : undefined}
                >
                  <UserCircle2 size={20} aria-hidden="true" />
                  <span className="sidebar-item-label">My Profile</span>
                </Link>
              </li>
            )}
            <li>
              <LogoutButton />
            </li>
          </ul>
        </div>
      </nav>
    </>
  );
};

export default Sidebar;
